/**
 * Admin V3 — Booking refund history modal (AV3 A-2, READ-ONLY)
 *
 * Surfaces the canonical refund ledger for a booking via
 * GET /payments/refund/history/{booking_id} (RBAC: payment:refund:history).
 * Read-only: no mutation, no side effect. Parity with the legacy V2
 * RefundHistoryModal. Loaded only while open.
 */
import React from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(typeof iso === 'string' ? new Date(iso) : iso);
  } catch { return String(iso); }
};

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 text-[13px]">
    <span className="text-slate-400">{label}</span>
    <span className="text-right font-medium text-slate-800">{children}</span>
  </div>
);

const BookingRefundHistoryModal = ({ open, bookingId, onClose, locale = DEFAULT_LOCALE }) => {
  const { data, loading } = useAdminQuery(
    `/payments/refund/history/${bookingId || '_'}`,
    {},
    { enabled: Boolean(open && bookingId) },
  );

  if (!open || !bookingId) return null;

  const events = Array.isArray(data?.refund_events) ? data.refund_events : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-booking-refund-history-modal">
      <div className="w-[520px] max-w-[94vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-slate-900">{t('bookings.refundHistory.title', locale)}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-booking-refund-history-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading && !data && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />{t('bookings.refundHistory.loading', locale)}
          </div>
        )}

        {data && (
          <div className="space-y-3">
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <Row label={t('bookings.refundHistory.original', locale)}>
                {Number(data.original_amount || 0).toFixed(2)} €
              </Row>
              <Row label={t('bookings.refundHistory.totalRefunded', locale)}>
                {Number(data.total_refunded || 0).toFixed(2)} €
              </Row>
              <Row label={t('bookings.refundHistory.status', locale)}>
                {data.refund_status || 'none'}
              </Row>
            </div>

            <div>
              <p className="mb-1 text-[12px] font-semibold text-slate-600">{t('bookings.refundHistory.events', locale)}</p>
              {events.length > 0 ? (
                <div className="space-y-2">
                  {events.map((ev, idx) => (
                    <div key={idx} className="rounded-lg border border-slate-100 p-2 text-[12px] text-slate-700" data-testid="adminv3-booking-refund-history-event">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {ev?.data?.amount != null ? `${Number(ev.data.amount).toFixed(2)} €` : (ev?.amount != null ? `${Number(ev.amount).toFixed(2)} €` : '—')}
                        </span>
                        <span className="text-slate-400">{fmtDateTime(ev.timestamp)}</span>
                      </div>
                      {(ev?.data?.reason || ev?.reason) && (
                        <div className="text-[11px] text-slate-400">{ev?.data?.reason || ev?.reason}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-slate-400" data-testid="adminv3-booking-refund-history-empty">
                  {t('bookings.refundHistory.empty', locale)}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-booking-refund-history-dismiss">
            {t('bookings.refundHistory.close', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingRefundHistoryModal;
